import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Icon from '../../components/AppIcon';
import Footer from '../../components/ui/Footer';

// =====================
// BLOG POST DATA
// =====================
// TO ADD A NEW BLOG POST:
// 1. Add a new object to this array with all the required fields
// 2. Create a corresponding page in /pages/blog/posts/[slug].jsx
// 3. Update the route in Routes.jsx

const blogPosts = [
    {
        id: 3,
        slug: 'hard-truth-about-finding-a-Job-in-India',
        title: 'The Hard Truth About Finding a Job in India Today: Why "Normal" Isn\'t Enough Anymore',
        excerpt: 'Stuck in the "Applied and Ghosted" loop? Discover why traditional job searching is failing Indian candidates in 2026.',
        author: 'JobAspirant Team',
        date: '2026-01-02',
        readTime: '6 min read',
        category: 'Career Strategy',
        image: '/images/blog/indian-job-market-truth.png',
    },
    {
        id: 2,
        slug: 'top-skills-employers-looking-for-2026',
        title: 'Top Skills Employers Are Looking For in 2026',
        excerpt: 'Discover the most in-demand skills across industries and how to showcase them on your resume.',
        author: 'JobAspirant Team',
        date: '2026-01-20',
        readTime: '7 min read',
        category: 'Career Advice',
        image: '/images/blog/skills-2026-india.png',
    },
];

const BlogListing = () => {
    return (
        <>
            <Helmet>
                <title>Career & Resume Tips Blog | JobAspirant</title>
                <meta
                    name="description"
                    content="Expert career advice, resume tips, and job search strategies to help you land your dream job. Stay updated with the latest trends in hiring and recruitment."
                />
                <meta name="keywords" content="resume tips, career advice, job search, ATS optimization, interview tips, job skills" />
                <link rel="canonical" href="https://jobaspirant.ai/blog" />
            </Helmet>

            <div className="min-h-screen bg-background flex flex-col">
                {/* Header */}
                <header className="border-b border-border bg-card">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex items-center justify-between">
                            <Link to="/" className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                                    <Icon name="Zap" size={24} color="white" />
                                </div>
                                <span className="text-2xl font-bold text-foreground">JobAspirant</span>
                            </Link>
                            <Link
                                to="/user-dashboard"
                                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                            >
                                Back to Dashboard
                            </Link>
                        </div>
                    </div>
                </header>

                {/* Hero Section */}
                <div className="bg-gradient-to-br from-primary/10 to-primary/5 border-b border-border">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                        <div className="text-center">
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4">
                                Career & Resume Tips
                            </h1>
                            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
                                Expert advice to help you stand out in your job search and advance your career
                            </p>
                        </div>
                    </div>
                </div>

                {/* Blog Posts Grid */}
                <div className="flex-1 py-12 sm:py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        {blogPosts.length === 0 ? (
                            <div className="text-center py-12">
                                <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
                                <h3 className="text-xl font-semibold text-foreground mb-2">No blog posts yet</h3>
                                <p className="text-muted-foreground">Check back soon for career tips and advice!</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {blogPosts.map((post) => (
                                    <Link
                                        key={post.id}
                                        to={`/blog/${post.slug}`}
                                        className="group bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200"
                                    >
                                        {/* Blog Image */}
                                        <div className="aspect-video bg-muted overflow-hidden">
                                            <img
                                                src={post.image}
                                                alt={post.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                                onError={(e) => {
                                                    // Fallback if image doesn't exist
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                                                    e.target.parentElement.innerHTML = '<div class="text-4xl">📝</div>';
                                                }}
                                            />
                                        </div>

                                        {/* Blog Content */}
                                        <div className="p-6">
                                            <div className="flex items-center space-x-2 mb-3">
                                                <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded">
                                                    {post.category}
                                                </span>
                                                <span className="text-xs text-muted-foreground">{post.readTime}</span>
                                            </div>

                                            <h2 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                                                {post.title}
                                            </h2>

                                            <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                                                {post.excerpt}
                                            </p>

                                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                                                <span>{post.author}</span>
                                                <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default BlogListing;
